import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { BUSINESS, SERVICES } from '../config/business';

const {
  FiPhone, FiMail, FiMapPin, FiClock, FiCheck, FiArrowRight,
  FiChevronDown, FiLoader, FiCalendar, FiAlertCircle, FiShield
} = FiIcons;

const CONTACT_LOG_API_URL = (import.meta.env.VITE_CONTACT_LOG_API_URL || '').trim();

const CONTACT_CONSENT_TEXT =
  'I agree to submit my enquiry to BPA Solutions, and I consent to receive relevant BPA Solutions tips, tools, updates, and service information. I understand I can unsubscribe at any time.';

const serviceAreas = [
  'Sydney CBD', 'Surry Hills', 'Parramatta', 'North Sydney',
  'Northern Beaches', 'Eastern Suburbs', 'Western Sydney',
  'Melbourne', 'Brisbane', 'Australia-wide'
];

function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [showConsentWarning, setShowConsentWarning] = useState(false);

  const initialFormState = {
    name: '',
    email: '',
    phone: '',
    businessName: '',
    suburb: '',
    serviceOfInterest: '',
    improvements: '',
    preferredDate: '',
    preferredTimeWindow: ''
  };

  const [formData, setFormData] = useState(initialFormState);

  const contactInfo = [
    { icon: FiMail, label: 'Email', value: BUSINESS.email, href: `mailto:${BUSINESS.email}` },
    { icon: FiPhone, label: 'Phone', value: BUSINESS.phoneFormatted || BUSINESS.phone, href: `tel:${BUSINESS.phone}` },
    { icon: FiMapPin, label: 'Location', value: BUSINESS.location, href: null }
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendEmail = async (payload) => {
    const apiKey = import.meta.env.VITE_EMAILIT_API_KEY;
    const response = await fetch('https://api.emailit.com/v1/emails', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Email delivery failed (${response.status})`);
    return response.json();
  };

  const logToGoogleSheets = async (selectedService) => {
    if (!CONTACT_LOG_API_URL) {
      console.warn('Contact lead logging URL is not configured.');
      return;
    }
    try {
      await fetch(CONTACT_LOG_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          action: 'log_contact_lead',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          businessName: formData.businessName,
          suburb: formData.suburb,
          serviceOfInterest: formData.serviceOfInterest,
          serviceName: selectedService,
          preferredDate: formData.preferredDate,
          preferredTimeWindow: formData.preferredTimeWindow,
          improvements: formData.improvements,
          consentGiven: consentGiven === true,
          consentText: CONTACT_CONSENT_TEXT,
          consentVersion: 'contact-form-consent-v1',
          consentTimestamp: new Date().toISOString(),
          sourcePage: 'Contact',
          userAgent: navigator.userAgent
        })
      });
    } catch (err) {
      console.warn('Google Sheets lead logging failed:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!consentGiven) {
      setShowConsentWarning(true);
      return;
    }

    setShowConsentWarning(false);
    setIsSubmitting(true);
    setErrorMessage('');
    setIsSuccess(false);

    const selectedService =
      SERVICES.find((s) => s.slug === formData.serviceOfInterest)?.name ||
      'General Automation Audit';

    try {
      setSubmissionStatus('Notifying our team');

      const adminEmail = {
        from: 'BPAS Website Lead <alerts@bpasolutions.au>',
        to: 'anish.m.malhotra@gmail.com',
        reply_to: formData.email,
        subject: `New Lead: ${selectedService} - ${formData.name}`,
        html: `<div style="font-family: sans-serif;color: #333;max-width: 600px;border: 1px solid #eee;padding: 20px;border-radius: 10px;">
          <h2 style="color: #2563eb;border-bottom: 2px solid #f3f4f6;padding-bottom: 10px;">New Website Enquiry</h2>
          <p><strong>Customer:</strong> ${formData.name}</p>
          <p><strong>Email:</strong> ${formData.email}</p>
          <p><strong>Phone:</strong> ${formData.phone || 'N/A'}</p>
          <p><strong>Business:</strong> ${formData.businessName || 'N/A'}</p>
          <p><strong>Suburb:</strong> ${formData.suburb || 'N/A'}</p>
          <p><strong>Service:</strong> ${selectedService}</p>
          <p><strong>Pref. Date:</strong> ${formData.preferredDate || 'Not specified'}</p>
          <p><strong>Pref. Time:</strong> ${formData.preferredTimeWindow || 'Not specified'}</p>
          <div style="background: #f9fafb;padding: 15px;border-radius: 8px;">
            <p><strong>Goals:</strong> ${formData.improvements || 'N/A'}</p>
          </div>
          <p style="text-align: center;color: #94a3b8;font-size: 12px;margin-top: 20px;">Enquiry Submitted On: bpasolutions.au</p>
        </div>`
      };

      await sendEmail(adminEmail);

      setSubmissionStatus('Preparing your confirmation email');
      await new Promise((resolve) => setTimeout(resolve, 4000));

      setSubmissionStatus('Sending confirmation to your email');

      const userEmail = {
        from: `${BUSINESS.name} <support@bpasolutions.au>`,
        to: formData.email,
        subject: `Enquiry Acknowledgement - ${BUSINESS.shortName}`,
        html: `<div style="font-family: sans-serif;color: #334155;max-width: 600px;margin: 0 auto;border: 1px solid #e2e8f0;border-radius: 12px;overflow: hidden;">
          <div style="background-color: #2563eb;padding: 30px;text-align: center;color: white;">
            <div style="margin: 0;font-size: 28px;font-weight: 700;">Enquiry Acknowledgement</div>
          </div>
          <div style="padding: 30px;">
            <p>Hi ${formData.name},</p>
            <p>We have received your request for <strong>${selectedService}</strong>. A team member will review the details and be in touch within 24 hours.</p>
            <p style="font-size: 13px;color: #64748b;">Please also check your spam or junk folder if you do not see this confirmation. We take care to send clean, professional emails, but recipient inbox settings can sometimes move messages to spam or junk.</p>
            <div style="text-align: center;margin: 40px 0;">
              <a href="https://tidycal.com/anishmmalhotra/initial-20-minutes-discussion" style="background: #2563eb;color: white;padding: 15px 25px;text-decoration: none;border-radius: 8px;font-weight: bold;">Schedule Discussion</a>
            </div>
          </div>
        </div>`
      };

      await sendEmail(userEmail);

      setIsSuccess(true);
      setFormData(initialFormState);
      setConsentGiven(false);
      setSubmissionStatus('');

      logToGoogleSheets(selectedService);
    } catch (error) {
      console.error('Submission failed:', error);
      setErrorMessage(
        'We encountered an error processing your request. Please try again or call us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled =
    isSubmitting ||
    !formData.name ||
    !formData.email ||
    !formData.serviceOfInterest ||
    !consentGiven;

  const inputClasses =
    'w-full px-4 py-3.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white text-slate-900 appearance-none disabled:bg-slate-50 disabled:text-slate-500 placeholder:text-slate-400 text-sm sm:text-base';

  return (
    <div className="overflow-x-hidden">
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-12 sm:py-20 text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-6 leading-tight">
            Get Your Free Automation Plan
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed px-4">
            Let's identify your biggest time-saving opportunities.
          </p>
          <p className="mt-4 text-sm text-slate-500 font-medium">
            Based in Sydney, helping businesses across Australia.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-10 relative">
              <h2 className="text-2xl font-bold text-slate-900 mb-8 flex items-center">
                <span className="w-1.5 h-6 bg-blue-600 rounded-full mr-3"></span>
                Tell Us About Your Business
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className={inputClasses}
                      placeholder="John Citizen"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={inputClasses}
                      placeholder="john@company.com.au"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={inputClasses}
                      placeholder="0400 000 000"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Business Name</label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      className={inputClasses}
                      placeholder="Acme Pty Ltd"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Suburb / State</label>
                    <input
                      type="text"
                      name="suburb"
                      value={formData.suburb}
                      onChange={handleInputChange}
                      className={inputClasses}
                      placeholder="Sydney, NSW"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-bold text-slate-700 mb-2">Service of Interest *</label>
                    <div className="relative">
                      <select
                        name="serviceOfInterest"
                        value={formData.serviceOfInterest}
                        onChange={handleInputChange}
                        required
                        className={inputClasses}
                        disabled={isSubmitting}
                      >
                        <option value="" disabled>Select a service...</option>
                        {SERVICES.map((s) => (
                          <option key={s.id} value={s.slug}>{s.name}</option>
                        ))}
                        <option value="other">Other / General Audit</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-slate-400">
                        <SafeIcon icon={FiChevronDown} className="h-5 w-5" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-5">
                  <h3 className="text-sm font-bold text-slate-900 flex items-center">
                    <SafeIcon icon={FiCalendar} className="mr-2.5 text-blue-600 h-5 w-5" />
                    Preferred Contact Time (Optional)
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">
                        Preferred Date
                      </label>
                      <input
                        type="date"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleInputChange}
                        className={inputClasses}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">
                        Preferred Time
                      </label>
                      <select
                        name="preferredTimeWindow"
                        value={formData.preferredTimeWindow}
                        onChange={handleInputChange}
                        className={inputClasses}
                        disabled={isSubmitting}
                      >
                        <option value="">Any time</option>
                        <option value="Morning (9am - 12pm)">Morning (9am - 12pm)</option>
                        <option value="Afternoon (12pm - 5pm)">Afternoon (12pm - 5pm)</option>
                        <option value="Evening (After 5pm)">Evening (After 5pm)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Automation Goals / Bottlenecks
                  </label>
                  <textarea
                    name="improvements"
                    value={formData.improvements}
                    onChange={handleInputChange}
                    rows={4}
                    className={inputClasses}
                    placeholder="Briefly describe your current manual bottlenecks..."
                    disabled={isSubmitting}
                  />
                </div>

                <div className="rounded-2xl border-2 border-blue-200 bg-blue-50 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <SafeIcon icon={FiShield} className="h-4 w-4 text-blue-600 shrink-0" />
                    <span className="text-xs font-extrabold text-blue-800 uppercase tracking-widest">
                      Required consent
                    </span>
                    <span className="text-rose-500 font-bold text-sm leading-none">*</span>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={consentGiven}
                      onChange={(e) => {
                        setConsentGiven(e.target.checked);
                        if (e.target.checked) setShowConsentWarning(false);
                      }}
                      className="mt-0.5 h-4 w-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
                      disabled={isSubmitting}
                      aria-required="true"
                    />
                    <span className="text-xs text-blue-900 leading-relaxed font-medium group-hover:text-blue-800 transition-colors">
                      {CONTACT_CONSENT_TEXT}
                    </span>
                  </label>
                </div>

                {showConsentWarning && (
                  <p className="text-xs text-amber-700 font-bold flex items-center gap-1.5 px-1">
                    <SafeIcon icon={FiAlertCircle} className="h-3.5 w-3.5 shrink-0" />
                    Please confirm consent before submitting your enquiry.
                  </p>
                )}

                <div className="space-y-5">
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className="w-full inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed shadow-lg transition-all active:scale-[0.98]"
                  >
                    {isSubmitting ? (
                      <>
                        <SafeIcon icon={FiLoader} className="animate-spin mr-3 h-5 w-5" />
                        Processing...
                      </>
                    ) : (
                      'Request My Automation Plan'
                    )}
                  </button>

                  <div aria-live="polite" className="text-center min-h-[28px] flex items-center justify-center">
                    {submissionStatus && (
                      <p className="text-blue-600 font-bold text-sm flex items-center">
                        <SafeIcon icon={FiLoader} className="animate-spin mr-2 h-4 w-4" />
                        {submissionStatus}
                      </p>
                    )}
                  </div>

                  <AnimatePresence>
                    {errorMessage && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        aria-live="assertive"
                        className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
                      >
                        <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                        <p className="text-sm text-red-800 font-bold">{errorMessage}</p>
                      </motion.div>
                    )}

                    {isSuccess && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="p-8 bg-green-50 border border-green-200 rounded-2xl text-center"
                      >
                        <div className="bg-green-100 h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-4">
                          <SafeIcon icon={FiCheck} className="h-7 w-7 text-green-600" />
                        </div>
                        <h4 className="text-green-900 font-extrabold text-xl mb-2">
                          Enquiry Successfully Sent!
                        </h4>
                        <p className="text-green-700 text-sm sm:text-base leading-relaxed mb-2">
                          Your enquiry has been submitted. Please check your inbox, spam, or junk folder for confirmation.
                        </p>
                        <p className="text-green-600 text-xs leading-relaxed">
                          We take care to send clean, professional emails, but recipient inbox settings can sometimes move messages to spam or junk.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </form>
            </div>
          </motion.div>

          <div className="space-y-8">
            <div className="bg-slate-900 text-white rounded-3xl p-8 sm:p-10 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <SafeIcon icon={FiClock} className="h-32 w-32" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-8 flex items-center text-blue-400">
                <SafeIcon icon={FiArrowRight} className="mr-3" />
                What Happens Next?
              </h3>
              <ul className="space-y-6 relative z-10">
                {[
                  'We analyze your provided details and current bottlenecks',
                  'A team member reviews your requirements for a custom roadmap',
                  'We contact you within 24 hours to discuss the next steps'
                ].map((step, i) => (
                  <li key={i} className="flex items-start space-x-4">
                    <div className="bg-blue-600/20 p-1 rounded-full shrink-0 mt-0.5">
                      <SafeIcon icon={FiCheck} className="h-5 w-5 text-blue-400" />
                    </div>
                    <span className="font-semibold text-slate-200 leading-relaxed text-sm sm:text-base">
                      {step}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-8 flex items-center">
                <span className="w-1.5 h-6 bg-blue-600 rounded-full mr-3"></span>
                Direct Contact
              </h3>
              <div className="space-y-8">
                {contactInfo.map((item, i) => (
                  <div key={i} className="flex items-center space-x-5 group">
                    <div className="bg-blue-50 p-3 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                      <SafeIcon icon={item.icon} className="h-6 w-6 text-blue-600 group-hover:text-white" />
                    </div>
                    <div>
                      <div className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest">
                        {item.label}
                      </div>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="text-slate-900 font-bold hover:text-blue-600 text-base sm:text-lg transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <div className="text-slate-900 font-bold text-base sm:text-lg">{item.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
              <h3 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2">
                <SafeIcon icon={FiMapPin} className="h-4 w-4 text-blue-600 shrink-0" />
                Website design and automation services across Sydney and Australia
              </h3>
              <p className="text-xs text-slate-500 leading-relaxed mb-4">
                BPA Solutions is based in Sydney and works with businesses across Australia, including
                professional services, trades, construction, consulting, property, healthcare, and local
                service businesses.
              </p>
              <div className="flex flex-wrap gap-2">
                {serviceAreas.map((area) => (
                  <span
                    key={area}
                    className="px-2.5 py-1 bg-white border border-slate-200 rounded-full text-[11px] font-medium text-slate-600"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;